/**
 * agreementContent.ts
 * 入会协议标准内容——单一数据源，MemberAudit、FileManager、MobileRegister 统一引用
 */

export const AGREEMENT_VERSION = "v2026.1";
export const AGREEMENT_TITLE = "球迷协会入会协议";

export interface AgreementSection {
  heading: string;
  items: string[];
}

export const AGREEMENT_SECTIONS: AgreementSection[] = [
  {
    heading: "一、权利",
    items: [
      "有权参加协会组织的观察、聚会、公益等各类活动；",
      "有权对协会工作提出建议、意见和监督；",
      "有权优先获取协会活动通知、门票福利、球迷周边等；",
      "普通会员不享有第一次成立大会的选举权、被选举权；",
      "入会自愿、退会自由，按规定办理即可。",
    ],
  },
  {
    heading: "二、义务",
    items: [
      "维护协会声誉和球迷群体形象，文明观赛、理性助威；",
      "不参与打架、起哄、辱骂、围堵等违规违纪行为；",
      "按时参加协会重要活动，因故无法到场及时说明；",
      "按协会规定缴纳会费，配合协会管理工作；",
      "不得擅自以协会名义从事商业、募捐等未经许可活动。",
    ],
  },
  {
    heading: "三、会员资格管理",
    items: [
      "会员信息由协会统一登记存档，仅限本人使用，不得转借、转让；",
      "会员如有严重违反本协议、协会章程或违法违规行为，协会可暂停或取消会员资格，并不退还会费；",
      "会员主动退会，需向协会管理人员说明并登记，手续办理后会员身份失效。",
    ],
  },
  {
    heading: "四、免责与安全",
    items: [
      "协会组织的户外活动、观赛活动具有一定风险性，会员自愿参加、自担风险；",
      "协会将尽力做好组织和安全提醒，但不承担会员个人过失导致的意外伤亡责任。",
    ],
  },
  {
    heading: "五、附则",
    items: [
      "本协议自签字之日起生效；",
      "本协议未尽事宜，参照协会章程及内部规定执行；",
      "本协议一式两份，协会与会员各持一份，具有同等效力。",
    ],
  },
];

/** 生成纯文本格式（用于手机端协议预览） */
export function buildAgreementPlainText(memberName = "_______________"): string {
  const sections = AGREEMENT_SECTIONS.map(
    (s) => `${s.heading}\n${s.items.map((item, i) => `${i + 1}. ${item}`).join("\n")}`
  ).join("\n\n");
  return (
    `${AGREEMENT_TITLE}\n\n` +
    `甲方（会员）：${memberName}\n` +
    `乙方（协会）：_______________（以主管机关登记及后台「基本信息」为准）\n\n` +
    sections +
    `\n\n甲方（会员签字）：_______________\n日期：_____年___月____日\n\n` +
    `乙方（协会经办人签字）：___________\n日期：_____年___月____日`
  );
}

/** 生成文件管理器 body 段落数组 */
export function buildAgreementBodyLines(): string[] {
  return [
    "甲方（会员）：_______________\n乙方（协会）：_______________（以登记及后台「基本信息」为准）",
    ...AGREEMENT_SECTIONS.map(
      (s) => `${s.heading}：${s.items.map((item, i) => `${i + 1}. ${item}`).join(" ")}`
    ),
    "甲方（会员签字）：_______________\u3000\u3000日期：_____年___月____日\n乙方（协会经办人签字）：___________\u3000\u3000日期：_____年___月____日",
  ];
}

/** 与后端 EnrollmentAgreementPdfBuilder 入会协议 PDF 页眉甲乙方格式一致 */
export function formatEnrollmentAgreementPartyALine(tenantName: string): string {
  const v = (tenantName ?? "").trim();
  return `甲方：${v || "—"}`;
}

export function formatEnrollmentAgreementPartyBLine(parts: {
  memberNo?: string | null;
  name?: string | null;
  idNumber?: string | null;
  phone?: string | null;
}): string {
  const d = (s?: string | null) => {
    const t = (s ?? "").trim();
    return t || "—";
  };
  return `乙方：（会员编号：${d(parts.memberNo)},姓名：${d(parts.name)}，身份证号：${d(parts.idNumber)}，手机号码：${d(parts.phone)}）`;
}
